import React from "react"

import { useTranslation } from "@bounce/i18n"
import { isFullPostAuthor } from "@bounce/web-components"

import { Article } from "./components/Article/Article"
import { CityList } from "./components/CityList"
import { Layout } from "./components/Layout"
import { Sidebar } from "./components/Sidebar"
import { useBreadcrumbs } from "./hooks/useBreadcrumbs"
import { useInterpolations } from "./hooks/useInterpolations"
import { ArticleSuggestions } from "../../../components/LuggageStorage/ArticleSuggestions/ArticleSuggestions"
import { ArticleStructuredData } from "@/components/Generic/StructuredData/StructuredData"
import type { CityData, TopCity } from "@/libs/bounce/types"
import type {
  ArticleType,
  FormattedAnyGuidePreview,
  FormattedBlogPost,
  FormattedBlogPostPreview,
} from "@/libs/prismic/types"
import { useUrl } from "@/libs/router"

import DEFAULT_ARTICLE_IMAGE from "@/assets/img/default_hero_2.png"

export type ArticleScreenProps = {
  topCities: TopCity[]
  article: FormattedBlogPost
  type: ArticleType
  city?: CityData | null
  poiSlug?: string
  languages: string[]
  relatedArticles?: FormattedBlogPostPreview[] | FormattedAnyGuidePreview[]
}

const ArticleScreen = ({ article, topCities, type, city, poiSlug, languages, relatedArticles }: ArticleScreenProps) => {
  const { absolutePath } = useUrl()

  const { t } = useTranslation("marketing-ls-city")
  const interpolations = useInterpolations({ city })
  const breadcrumbs = useBreadcrumbs({ type, article, city, poiSlug })
  const { title, datePublished, dateModified, image, product, author } = article
  const hasAuthor = isFullPostAuthor(author)

  const relatedArticlesTitle =
    article.type === "guide"
      ? t("screen.articlesScreen.articleSuggestions.relatedGuides", "Related guides")
      : t("screen.articlesScreen.articleSuggestions.relatedArticles", "Related articles")

  return (
    <Layout article={article} languages={languages}>
      <div className="relative mx-auto grid w-full max-w-screen-xl grid-flow-row grid-cols-1 xl:grid-cols-[900px,_1fr]">
        <div className="flex w-full max-w-[900px] flex-1 flex-col items-center">
          <Article article={article} breadcrumbs={breadcrumbs} interpolations={interpolations} />
        </div>
        <Sidebar product={product} />
      </div>
      {relatedArticles && relatedArticles.length > 0 && (
        <>
          <div className="w-full pt-section" />
          <ArticleSuggestions title={relatedArticlesTitle} relatedArticles={relatedArticles} />
        </>
      )}
      <CityList article={article} topCities={topCities} />
      <div className="w-full pt-section" />
      <ArticleStructuredData
        headline={title}
        datePublished={datePublished}
        dateModified={dateModified}
        image={image ?? DEFAULT_ARTICLE_IMAGE.src}
        url={absolutePath}
        author={
          hasAuthor
            ? {
                "@type": "Person",
                name: author.name,
                url: author.href,
              }
            : undefined
        }
      />
    </Layout>
  )
}

export default ArticleScreen
